import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import React, { FC, useEffect, useState } from "react";
import * as yup from "yup";
import { list } from "../../../client/api/collection";
import { provisionCourseDemo } from "../../../client/api/users";
import { useAlert } from "../../../contexts/alert.context";
import { StoryCollectionType } from "../../../models/modelTypes";
import { useAuth } from "../../../contexts/auth.context";
import { getRoleDisplayName } from "../entities/RoleType";
import largeV from "../../../img/large-v-nav.svg";

type CourseDemoForm = {
    firstName: string;
    lastName: string;
    email: string;
    storyCollectionId: number | "";
    organisationName: string;
};

export const CourseDemoProvisionCard: FC = () => {
    const { setAlert } = useAlert();
    const { user } = useAuth();
    const facilitatorLabel = getRoleDisplayName("Facilitator", user?.isSchool);
    const adminLabel = getRoleDisplayName("Admin", user?.isSchool);
    const [open, setOpen] = useState(false);
    const [collections, setCollections] = useState<StoryCollectionType[]>([]);

    useEffect(() => {
        list({ take: 200, skip: 0, courseDemoEligible: true })
            .then((result) => setCollections(result.items ?? []))
            .catch(() => setCollections([]));
    }, []);

    const onSubmit = async (values: CourseDemoForm, helpers: FormikHelpers<CourseDemoForm>) => {
        try {
            const response = await provisionCourseDemo({
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                storyCollectionId: Number(values.storyCollectionId),
                organisationName: values.organisationName || undefined,
            });
            if (response.ok) {
                setAlert(
                    `Course demo account created for ${values.firstName} ${values.lastName}. They will receive an email to set a password.`,
                    "success",
                    5000,
                );
                setOpen(false);
                helpers.resetForm();
            } else {
                const body = (await response.json().catch(() => null)) as { message?: string } | null;
                setAlert(body?.message ?? "Could not create course demo account.", "error");
            }
        } catch {
            setAlert("Could not create course demo account.", "error");
        } finally {
            helpers.setSubmitting(false);
        }
    };

    return (
        <>
            <Box
                display="flex"
                height="100%"
                width="100%"
                flexDirection="column"
                justifyContent="space-between"
                sx={{
                    color: "common.white",
                    p: 2,
                    bgcolor: "primary.dark",
                    backgroundImage: `url(${largeV})`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center bottom",
                    borderRadius: 2,
                }}
            >
                <Box>
                    <Typography variant="h4" align="center" gutterBottom>
                        Course demo trainee
                    </Typography>
                    <Typography paragraph>
                        Provision a personal sandbox org (Demo tier) with one cohort collection. Trainee is {adminLabel}{" "}
                        + {facilitatorLabel} and receives an invite email.
                    </Typography>
                </Box>
                <Button color="primary" variant="contained" fullWidth onClick={() => setOpen(true)}>
                    Provision course demo
                </Button>
            </Box>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Provision course demo account</DialogTitle>
                <DialogContent>
                    <Formik<CourseDemoForm>
                        initialValues={{
                            firstName: "",
                            lastName: "",
                            email: "",
                            storyCollectionId: "",
                            organisationName: "",
                        }}
                        validationSchema={yup.object({
                            firstName: yup.string().required("First Name is required"),
                            lastName: yup.string().required("Surname is required"),
                            email: yup
                                .string()
                                .email("A valid email is required")
                                .required("Email is required")
                                .max(100),
                            storyCollectionId: yup
                                .number()
                                .typeError("Collection is required")
                                .required("Collection is required"),
                            organisationName: yup.string().max(50),
                        })}
                        validateOnChange={false}
                        onSubmit={onSubmit}
                    >
                        {({ handleSubmit, handleChange, values, errors, touched, isSubmitting, setFieldValue }) => (
                            <Form onSubmit={handleSubmit}>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            required
                                            name="firstName"
                                            label="First Name"
                                            variant="outlined"
                                            fullWidth
                                            value={values.firstName}
                                            onChange={handleChange}
                                            error={!!errors.firstName}
                                            helperText={touched.firstName && errors.firstName}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            required
                                            name="lastName"
                                            label="Surname"
                                            variant="outlined"
                                            fullWidth
                                            value={values.lastName}
                                            onChange={handleChange}
                                            error={!!errors.lastName}
                                            helperText={touched.lastName && errors.lastName}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            required
                                            name="email"
                                            label="Email"
                                            variant="outlined"
                                            fullWidth
                                            value={values.email}
                                            onChange={handleChange}
                                            error={!!errors.email}
                                            helperText={touched.email && errors.email}
                                            inputProps={{ maxLength: 100 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth required error={!!errors.storyCollectionId}>
                                            <InputLabel id="course-demo-collection-label">Cohort collection</InputLabel>
                                            <Select
                                                labelId="course-demo-collection-label"
                                                label="Cohort collection"
                                                value={values.storyCollectionId}
                                                onChange={(e) => setFieldValue("storyCollectionId", e.target.value)}
                                            >
                                                {collections.map((c) => (
                                                    <MenuItem key={c.id} value={c.id}>
                                                        {c.name} ({c.ageRange})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                {errors.storyCollectionId ||
                                                    "Published collections that contain at least one published story"}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            name="organisationName"
                                            label="Personal org name (optional)"
                                            variant="outlined"
                                            fullWidth
                                            value={values.organisationName}
                                            onChange={handleChange}
                                            helperText="Defaults to trainee name + email if left blank"
                                            inputProps={{ maxLength: 50 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box display="flex" justifyContent="space-between">
                                            <Button
                                                variant="contained"
                                                disabled={isSubmitting}
                                                onClick={() => setOpen(false)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? <CircularProgress size={24} /> : "Provision"}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    );
};
